import { useState } from 'react';

import { jobsConfigApi, type TeamJob } from '@/api/queries/jobsConfig';
import { SettingField } from '@/components/jobs/SettingField';
import { Button, Field, Select, useToast } from '@/components/ui';

// Tri-state enable for the bool? override: inherit the global flag, or force on/off per tenant.
type EnableChoice = 'inherit' | 'on' | 'off';

function choiceFromEnabled(enabled: boolean | null): EnableChoice {
  if (enabled === null || enabled === undefined) return 'inherit';
  return enabled ? 'on' : 'off';
}

function enabledFromChoice(choice: EnableChoice): boolean | null {
  if (choice === 'inherit') return null;
  return choice === 'on';
}

/**
 * Settings tab of the tenant job detail page (/settings/jobs/$key): tri-state
 * enable override + per-tenant behaviour settings. The schedule stays global
 * (owned by super-admins on /admin/jobs), so there is no cron field here.
 */
export function TenantJobSettings({ job }: { job: TeamJob }) {
  const update = jobsConfigApi.useUpdateTenantJob();
  const toast = useToast();

  const [choice, setChoice] = useState<EnableChoice>(choiceFromEnabled(job.enabled));
  const [settings, setSettings] = useState<Record<string, unknown>>({ ...job.settings });

  const resolvedEnabled = job.globalEnabled && (choice === 'inherit' ? true : choice === 'on');

  const onSave = async () => {
    try {
      await update.mutateAsync({
        key: job.key,
        body: { enabled: enabledFromChoice(choice), settings },
      });
      toast.show({ title: 'Job settings saved', severity: 'success' });
    } catch (err) {
      toast.show({
        title: 'Failed to save',
        description: err instanceof Error ? err.message : undefined,
        severity: 'error',
      });
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 640 }}>
      {!job.globalEnabled && (
        <span style={{ fontSize: 12, color: 'var(--amber, var(--text-3))' }}>
          This job is currently disabled globally, so it will not run for any organisation regardless
          of this setting.
        </span>
      )}

      <Field
        label="Status"
        hint={
          choice === 'inherit'
            ? `Inherit the global setting (currently ${job.globalEnabled ? 'on' : 'off'}).`
            : resolvedEnabled
              ? 'This job runs for your organisation.'
              : 'This job will not run for your organisation.'
        }
      >
        <Select
          value={choice}
          onChange={(e) => setChoice(e.target.value as EnableChoice)}
          options={[
            { value: 'inherit', label: 'Inherit (default)' },
            { value: 'on', label: 'On' },
            { value: 'off', label: 'Off' },
          ]}
        />
      </Field>

      {job.settingsSchema.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <h4 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--text-2)' }}>Behaviour</h4>
          {job.settingsSchema.map((field) => (
            <SettingField
              key={field.key}
              field={field}
              value={settings[field.key]}
              onChange={(v) => setSettings((prev) => ({ ...prev, [field.key]: v }))}
            />
          ))}
        </div>
      )}

      <div>
        <Button variant="primary" onClick={onSave} disabled={update.isPending}>
          Save changes
        </Button>
      </div>
    </div>
  );
}
